/**
 * RequireHoursFieldset — the "Require Hours" opt-in plus its hour bucket.
 *
 * Ticking the box turns any work order into an hour-tracked job: the
 * Assignment section swaps its standard assignee pickers for the
 * per-assignee hours editor (TmAssigneeHoursFieldset), every assignee has
 * to carry hours, and the record can't be handed off for review or
 * completed until they're filled in.
 *
 * Before this existed, that behavior was reachable only by picking the
 * 't_and_m' Business Line sub-type — so a job that merely needed hour
 * tracking had to be mislabeled as T&M to get it. T&M itself is unchanged:
 * it still forces the behavior on, which is why `locked` renders the box
 * ticked and disabled rather than hiding it. Showing it locked tells the
 * user the rule exists and that the sub-type is what's driving it.
 *
 * The bucket is the pool of hours the work order draws from. It persists to
 * work_orders.allocated_hours — the same budget column a child WO of a
 * Project uses for its slice of the parent's pool, because both mean
 * "hours this work order is allowed to draw". That sharing is also why
 * `showBucket` exists: on a project child the Project Assignment panel
 * above the form already owns that field, so this one steps aside rather
 * than offering a second input for one column.
 *
 * Required on T&M (the number comes from the contract with the customer),
 * optional everywhere else — there it's a reporting/export nicety, so the
 * copy says so instead of nagging for it.
 *
 * Fully controlled; the parent (JobForm) owns both values.
 */
import React from 'react';
import { Checkbox } from '../../atoms';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface RequireHoursFieldsetProps {
	/** Whether hour tracking is on for this work order. */
	checked: boolean;
	onCheckedChange: (checked: boolean) => void;

	/** When true the box renders ticked + disabled — T&M forces it on. */
	locked?: boolean;
	/** Short parenthetical explaining WHY it's locked. */
	lockedNote?: string;

	/** Bucket of hours, buffered as a string so the input can be empty
	 *  mid-typing without coercing to 0. */
	bucketHours: string;
	onBucketHoursChange: (value: string) => void;
	/** Whether to render the bucket input at all. False when something
	 *  else on the page already owns work_orders.allocated_hours. */
	showBucket?: boolean;
	/** Whether the bucket must be filled in (T&M only). */
	bucketRequired?: boolean;
	/** Shown in place of the input when showBucket is false, to explain
	 *  where the number is being entered instead. */
	bucketElsewhereNote?: string;

	/** Inline validation errors keyed by field name. */
	errors?: Record<string, string>;
	/** Standard styling tokens. */
	labelClass: string;
	inputClass: string;
	normalBorder: string;
	errorBorder: string;
	/** Optional extra classes for the outer wrapper. */
	className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const RequireHoursFieldset: React.FC<RequireHoursFieldsetProps> = ({
	checked,
	onCheckedChange,
	locked = false,
	lockedNote,
	bucketHours,
	onBucketHoursChange,
	showBucket = true,
	bucketRequired = false,
	bucketElsewhereNote,
	errors,
	labelClass,
	inputClass,
	normalBorder,
	errorBorder,
	className = '',
}) => {
	/* A locked fieldset is always on regardless of the stored flag, so the
	   checkbox never contradicts the rule the form is actually applying. */
	const isChecked = locked || checked;

	return (
		<div className={`require-hours-fieldset mb-16px pb-16px border-b border-brand-border ${className}`} data-ref="molecule-require-hours-fieldset">
			{/* --- The opt-in itself --- */}
			<Checkbox
				checked={isChecked}
				disabled={locked}
				onChange={onCheckedChange}
				className="require-hours-fieldset__toggle py-10px"
				labelClassName="font-medium text-gray-700"
				dataRef="molecule-require-hours-fieldset-toggle"
				label={(
					<>
						Require Hours
						{locked && lockedNote && (
							<span className="text-12px font-normal text-brand-text-muted ml-4px">{lockedNote}</span>
						)}
					</>
				)}
				description="Track this work order like a T&M job — record who worked on it and how many hours each person put in. It can't be submitted or completed until those hours are entered."
			/>

			{/* --- Hour bucket. Only meaningful once tracking is on, so it
			    stays out of the way until the box is ticked. --- */}
			{isChecked && showBucket && (
				<div className="require-hours-fieldset__bucket mt-12px max-w-[280px]">
					<label htmlFor="hoursBucket" className={labelClass}>
						Bucket of Hours{bucketRequired && <span className="text-red-500"> *</span>}
					</label>
					<input
						id="hoursBucket"
						type="number"
						min={0}
						step={0.25}
						value={bucketHours}
						onChange={(e) => onBucketHoursChange(e.target.value)}
						placeholder="0"
						className={`${inputClass} ${errors?.hoursBucket ? errorBorder : normalBorder}`}
					/>
					{errors?.hoursBucket ? (
						<p className="mt-4px text-14px text-red-600">{errors.hoursBucket}</p>
					) : (
						<p className="mt-4px text-12px font-sans text-brand-text-muted">
							{bucketRequired
								? 'The hours contracted with the customer for this T&M work order. Assigned hours are measured against it.'
								: 'Optional. The pool of hours this work order draws from — entering it improves reporting and exports.'}
						</p>
					)}
				</div>
			)}

			{/* --- Bucket owned elsewhere on the page (project children). --- */}
			{isChecked && !showBucket && bucketElsewhereNote && (
				<p className="require-hours-fieldset__bucket-note mt-12px text-12px font-sans text-brand-text-muted">
					{bucketElsewhereNote}
				</p>
			)}
		</div>
	);
};

export default RequireHoursFieldset;
